
import { Component, Vue } from "vue-property-decorator";
import { uiStore, singularityStore, documentStore } from "@/store/store";
import SingularityCanvasButton from "./SingularityCanvasButton.vue";
import { getElementName } from "@/lib/elements/utils";
import {
  GRID_SLUG,
  IMAGE_SLUG,
  SECTION_SLUG,
  ICON_SLUG,
  GALLERY_SLUG
} from "@/lib/elements/constants";
import lodash from "lodash";
import { findNode } from "@/lib/tree/tree";

import {
  isChildOfLinkElement,
  isLinkElement,
  getNearestParentLinkElement,
  getLinkPropertyPathsForElement
} from "@/futurelayer/links/links";

import {
  FUTURELAYER_HEADER_ELEMENT_TYPE,
  FUTURELAYER_FOOTER_ELEMENT_TYPE,
  FUTURELAYER_SITELOGO_ELEMENT_TYPE
} from "@/futurelayer/headers-footers/headers-footers";

@Component({
  components: {
    SingularityCanvasButton
  }
})
export default class EditElementCanvasButton extends Vue {
  get element() {
    return uiStore.activeElement;
  }

  get elementId() {
    return uiStore.activeElementId;
  }

  getElementTitle() {
    if (!this.element) return "";
    return getElementName(this.element);
  }

  get elementTitle() {
    return this.getElementTitle();
  }

  get isImageElement() {
    // lack of suport for legacy images (LEGACY_IMAGE_SLUG) is deliberate
    return this.element && this.element.data.type === IMAGE_SLUG;
  }

  get isSectionElementWithBackgroundImage() {
    return (
      this.element &&
      this.element.data.type === SECTION_SLUG &&
      lodash.get(this.element.data.properties, "design.background.type") ==
        "image"
    );
  }

  get isIconElement() {
    return this.element && this.element.data.type === ICON_SLUG;
  }

  get isChildOfGrid() {
    if (!this.element) return false;

    const parent = findNode(
      documentStore.document.tree,
      this.element._parentId
    );

    if (!parent) return false;

    return parent.data.type === GRID_SLUG;
  }

  get isGalleryElement() {
    return this.element && this.element.data.type === GALLERY_SLUG;
  }

  get isLinkElementOrChildOfLinkElement() {
    if (!this.element) return false;
    return (
      isLinkElement(this.element.data.type) ||
      isChildOfLinkElement(this.element)
    );
  }

  get isHeaderElement() {
    return (
      this.element && this.element.data.type === FUTURELAYER_HEADER_ELEMENT_TYPE
    );
  }

  get isFooterElement() {
    return (
      this.element && this.element.data.type === FUTURELAYER_FOOTER_ELEMENT_TYPE
    );
  }

  get isHeaderOrFooterElement() {
    return this.isHeaderElement || this.isFooterElement;
  }

  get isSiteLogoElement() {
    return (
      this.element &&
      this.element.data.type === FUTURELAYER_SITELOGO_ELEMENT_TYPE
    );
  }

  editElement() {
    if (!this.element || !this.elementId) return;

    if (this.isSiteLogoElement) {
      singularityStore.setSingularityLeftPanel("brand");
      return;
    }

    if (this.isHeaderOrFooterElement) {
      singularityStore.setHeaderAndFooterEditorPanelState({
        elementId: this.elementId,
        elementType: this.isHeaderElement ? "header" : "footer"
      });
      return;
    }

    if (this.isImageElement) {
      singularityStore.setImageEditorPanelState({
        elementId: this.elementId,
        controlPath: "content.image.media",
        initialStep: "main"
      });
      singularityStore.setSingularityLeftPanel("image_editor");
      return;
    }

    if (this.isGalleryElement) {
      singularityStore.setImageEditorPanelState({
        elementId: this.elementId,
        controlPath: "content.content.images[].image",
        selectedImageIndex: 0,
        initialStep: "main"
      });
      singularityStore.setSingularityLeftPanel("image_editor");
      return;
    }

    if (this.isIconElement) {
      singularityStore.setIconEditorPanelState({
        iconId: this.elementId
      });
      singularityStore.setSingularityLeftPanel("icon_editor");
      return;
    }

    if (this.isSectionElementWithBackgroundImage) {
      singularityStore.setImageEditorPanelState({
        elementId: this.elementId,
        controlPath: "design.background.image",
        initialStep: "main"
      });
      singularityStore.setSingularityLeftPanel("image_editor");

      return;
    }

    if (this.isLinkElementOrChildOfLinkElement) {
      this.editLink();
      return;
    }

    // Default editor
    singularityStore.setContentPropertiesEditorPanelState(true);
    singularityStore.setSingularityLeftPanel("content_properties_editor");
  }

  editLink() {
    if (!this.elementId) return;

    let element = findNode(documentStore.document.tree, this.elementId);
    if (!element) return;

    if (!isLinkElement(element.data.type)) {
      element = getNearestParentLinkElement(element);
    }

    if (!element) return;
    if (!isLinkElement(element.data.type)) return;

    const linkPropertyPaths = getLinkPropertyPathsForElement(element.data.type);
    if (!linkPropertyPaths) return;

    singularityStore.setLinkEditorPanelState({
      elementId: element.id,
      linkPropertyPaths: linkPropertyPaths
    });
    singularityStore.setSingularityLeftPanel("link_editor");
  }
}
